import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from './auth';

/**
 * VERAX: Inteligencia y reglas aprendidas
 *
 * Cada día el sistema recoge inteligencia de fuentes públicas confiables, la
 * cruza con las marcas vigiladas y propone reglas. Las propuestas no cambian
 * la detección hasta que un ADMIN las activa aquí.
 */

interface Run { id: string; startedAt: string; finishedAt: string | null; itemsNew: number; proposals: number; error: string | null;
  sources: Record<string, { ok: boolean; items?: number; nuevos?: number; error?: string }> | null }
interface Fuente { id: string; name: string; url: string; purpose: string; enabled: boolean }
interface Status { runs: Run[]; reglasPropuestas: number; reglasActivas: number; elementos: number; coincidencias: number; fuentes: Fuente[] }
interface Regla { id: string; kind: string; value: string; status: string; rationale: string; evidenceCount: number;
  samples: string[]; proposedAt: string; decidedBy: string | null; decidedAt: string | null; company: { name: string } | null }
interface Item { id: string; source: string; kind: string; value: string; title: string | null; link: string | null;
  collectedAt: string; matchReason: string | null; matchedCompanyId: string | null }

const KIND: Record<string, string> = {
  KEYWORD: 'Palabra señuelo', ABUSIVE_TLD: 'TLD de abuso', SECTOR_TERM: 'Término de sector', BRAND_TERM: 'Término de marca'
};
const fecha = (iso: string | null) => iso ? new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

export const IntelligencePanel: React.FC<{ puedeDecidir: boolean }> = ({ puedeDecidir }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [reglas, setReglas] = useState<Regla[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [msg, setMsg] = useState('');
  const [corriendo, setCorriendo] = useState(false);

  const cargar = useCallback(() => {
    axios.get(`${API}/intel/status`).then(r => setStatus(r.data)).catch(() => setMsg('No se pudo cargar el estado'));
    axios.get(`${API}/intel/rules`).then(r => setReglas(r.data)).catch(() => undefined);
    axios.get(`${API}/intel/items`, { params: { matched: 1 } }).then(r => setItems(r.data)).catch(() => undefined);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const decidir = async (id: string, accion: 'approve' | 'reject') => {
    try {
      await axios.post(`${API}/intel/rules/${id}/${accion}`);
      setMsg(accion === 'approve' ? 'Regla activada: los motores ya la usan.' : 'Regla descartada.');
      cargar();
    } catch (e: any) { setMsg(e?.response?.data?.error || 'No se pudo registrar la decisión'); }
  };

  const correr = async () => {
    setCorriendo(true); setMsg('Recogiendo inteligencia…');
    try {
      const r = await axios.post(`${API}/intel/run`);
      setMsg(`${r.data.itemsNew} elementos nuevos · ${r.data.proposals} reglas propuestas · ${r.data.evaluated} dominios verificados`);
      cargar();
    } catch (e: any) { setMsg(e?.response?.data?.error || 'El ciclo falló'); }
    finally { setCorriendo(false); }
  };

  const ultima = status?.runs[0];
  const propuestas = reglas.filter(r => r.status === 'PROPOSED');
  const activas = reglas.filter(r => r.status === 'ACTIVE');

  return (
    <div>
      <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Inteligencia</h2>
          <p className="text-sm text-gray-400">Aprendizaje diario desde fuentes públicas confiables. Las reglas nuevas se activan solo con aprobación.</p>
        </div>
        {puedeDecidir && (
          <button onClick={correr} disabled={corriendo} className="bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-sm px-4 py-2 rounded">
            {corriendo ? 'Corriendo…' : 'Correr ciclo ahora'}
          </button>
        )}
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300">{msg}</p>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          ['Última corrida', ultima ? fecha(ultima.startedAt) : 'nunca'],
          ['Elementos en 90 días', status?.elementos ?? '-'],
          ['Coincidencias con marcas', status?.coincidencias ?? '-'],
          ['Reglas activas / propuestas', status ? `${status.reglasActivas} / ${status.reglasPropuestas}` : '-']
        ].map(([l, v]) => (
          <div key={String(l)} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <p className="text-xs text-gray-400">{l}</p><p className="text-xl font-bold mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 mb-5">
        <h4 className="text-sm font-semibold mb-3">Reglas propuestas</h4>
        {propuestas.length === 0 && <p className="text-sm text-gray-500">No hay propuestas pendientes.</p>}
        <div className="space-y-3">
          {propuestas.map(r => (
            <div key={r.id} className="border border-gray-800 rounded p-3">
              <div className="flex justify-between gap-3 flex-wrap">
                <div>
                  <span className="text-xs bg-gray-800 px-2 py-0.5 rounded mr-2">{KIND[r.kind] ?? r.kind}</span>
                  <span className="font-mono font-bold">{r.value}</span>
                  <span className="text-xs text-gray-500 ml-2">{r.company ? r.company.name : 'todas las marcas'}</span>
                </div>
                {puedeDecidir && (
                  <div className="flex gap-2">
                    <button onClick={() => decidir(r.id, 'approve')} className="text-xs bg-emerald-800 hover:bg-emerald-700 px-3 py-1 rounded">Activar</button>
                    <button onClick={() => decidir(r.id, 'reject')} className="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded">Descartar</button>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-300 mt-2">{r.rationale}</p>
              {r.samples.length > 0 && <p className="text-[11px] text-gray-500 mt-1 font-mono break-all">Ej.: {r.samples.join(' · ')}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold mb-3">Reglas activas</h4>
          {activas.length === 0 && <p className="text-sm text-gray-500">Ninguna todavía.</p>}
          <ul className="text-sm space-y-1">
            {activas.map(r => (
              <li key={r.id}><span className="font-mono">{r.value}</span> <span className="text-xs text-gray-500">· {KIND[r.kind]} · {r.company?.name ?? 'global'} · activó {r.decidedBy} el {fecha(r.decidedAt)}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold mb-3">Fuentes</h4>
          <ul className="text-sm space-y-1">
            {status?.fuentes.map(f => {
              const s = ultima?.sources?.[f.id];
              return (
                <li key={f.id} className="flex justify-between gap-2">
                  <span>{f.name} <span className="text-xs text-gray-500">· {f.purpose}</span></span>
                  <span className={`text-xs ${!s ? 'text-gray-500' : s.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {!s ? '-' : s.ok ? `${s.items} leídos · ${s.nuevos} nuevos` : `sin respuesta (${s.error})`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h4 className="text-sm font-semibold mb-3">Coincidencias y avisos recientes</h4>
        {items.length === 0 && <p className="text-sm text-gray-500">Sin coincidencias con las marcas vigiladas.</p>}
        <ul className="text-sm space-y-1">
          {items.map(i => (
            <li key={i.id} className="flex justify-between gap-3">
              <span className="break-all">
                {i.kind === 'ADVISORY'
                  ? <a href={i.link ?? '#'} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">{i.title}</a>
                  : <span className="font-mono text-red-300">{i.value}</span>}
                <span className="text-xs text-gray-500"> · {i.source}{i.matchReason ? ` · ${i.matchReason}` : ''}</span>
              </span>
              <span className="text-xs text-gray-500 whitespace-nowrap">{fecha(i.collectedAt)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
